import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  Snackbar,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  LocalAtm as CashIcon,
  Nfc as NfcIcon,
  Payments as UpiIcon,
  PointOfSale as PointOfSaleIcon,
  Restaurant as RestaurantIcon,
} from "@mui/icons-material";
import { branchAPI, salesPointAPI, saleAPI } from "../../services";
import { productSalesPointAPI } from "../../services/productSalesPointAPI";
import { tableAPI } from "../../services/tableAPI";
import { nfcCardAPI } from "../../services/nfcCardAPI";
import { useAuthStore } from "../../stores/authStore";
import { useRBAC } from "../../hooks/useRBAC";
import NfcCardScanInput from "../../components/nfc/NfcCardScanInput";
import AppLogo from "../../components/brand/AppLogo";
import { petpooja } from "./posPetpoojaTheme";
import { getApiErrorMessage } from "../../utils/apiError";
import {
  CartItem,
  CustomerDetails,
  Product,
  Table,
  normalizePosTable,
} from "./types";
import TablePanel from "./components/TablePanel";
import ProductsPanel from "./components/ProductsPanel";
import CartPanel from "./components/CartPanel";
import CustomerDialog from "./components/CustomerDialog";
import KOTDialog from "./components/KOTDialog";

type PaymentMethod = "cash" | "upi" | "card";

const toAmount = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const SalesPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { hasAnyRole, canChangePosScope, canCreate } = useRBAC();
  const isMerchant = hasAnyRole("MERCHANT");
  const canChangeScope = canChangePosScope();

  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [activeSaleId, setActiveSaleId] = useState<string | null>(null);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [customerDialog, setCustomerDialog] = useState(false);
  const [kotDialog, setKotDialog] = useState(false);
  const [kotNumber, setKotNumber] = useState<string | null>(null);
  const [settleOpen, setSettleOpen] = useState(false);
  const [settleError, setSettleError] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    isMerchant ? "card" : "cash"
  );
  const scannedCardRef = useRef("");
  const [scanBuffer, setScanBuffer] = useState("");
  const [cardScanSession, setCardScanSession] = useState(0);
  const [nfcCardId, setNfcCardId] = useState<string | null>(null);
  const [cardInfo, setCardInfo] = useState<{
    displayNumber: string;
    balance: number;
    holderName?: string;
  } | null>(null);
  const [cardScanLoading, setCardScanLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    severity: "success" | "error";
  } | null>(null);
  const [customerDetails, setCustomerDetails] = useState<CustomerDetails>({
    name: "",
    phone: "",
    email: "",
  });
  const [selectedBranch, setSelectedBranch] = useState(user?.branchId ?? "");
  const [selectedSalesPoint, setSelectedSalesPoint] = useState(
    user?.salesPointId ?? ""
  );
  const [billingSalesPointId, setBillingSalesPointId] = useState(
    user?.salesPointId ?? ""
  );
  const [clock, setClock] = useState(() =>
    new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setClock(
        new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
      );
    }, 30_000);
    return () => clearInterval(timer);
  }, []);

  const { data: branches = [], isLoading: branchesLoading } = useQuery({
    queryKey: ["branches", "all"],
    queryFn: () => branchAPI.getAllBranches(),
    enabled: canChangeScope || !!user?.branchId,
  });

  const { data: salesPoints = [], isLoading: salesPointsLoading } = useQuery({
    queryKey: ["sales_points", selectedBranch],
    queryFn: () =>
      selectedBranch
        ? salesPointAPI.getSalesPointsByBranch(selectedBranch)
        : [],
    enabled: !!selectedBranch,
  });

  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ["restaurant-products", billingSalesPointId],
    queryFn: () =>
      productSalesPointAPI.listBySalesPoint(billingSalesPointId, {
        pageSize: 500,
      }),
    enabled: !!billingSalesPointId,
  });

  const {
    data: tablesData,
    isLoading: tablesLoading,
    isError: tablesError,
    refetch: refetchTables,
  } = useQuery({
    queryKey: ["restaurant-tables", selectedBranch, selectedSalesPoint],
    queryFn: () =>
      tableAPI.getTables({
        branchId: selectedBranch,
        salesPointId: selectedSalesPoint,
      }),
    enabled: !!selectedBranch && !!selectedSalesPoint,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });

  const products: Product[] =
    productsData?.data.map((p) => ({
      id: p.id,
      productMasterId: p.productMasterId,
      name: p.name,
      salePrice: p.sellingPrice,
      category: p.category?.name || "General",
      gstPercentage: Number(p.gstPercentage) || 0,
      isTaxInclusive: Boolean(p.isTaxInclusive),
    })) ?? [];

  const tables: Table[] = (tablesData?.tables ?? []).map((t) =>
    normalizePosTable(t)
  );

  const branchName = useMemo(
    () => branches.find((b) => b.id === selectedBranch)?.name || "Branch",
    [branches, selectedBranch]
  );
  const deptName = useMemo(
    () => salesPoints.find((d) => d.id === selectedSalesPoint)?.name || "Sales point",
    [salesPoints, selectedSalesPoint]
  );

  useEffect(() => {
    if (!canChangeScope && user) {
      if (user.branchId) setSelectedBranch(user.branchId);
      if (user.salesPointId) {
        setSelectedSalesPoint(user.salesPointId);
        if (!activeSaleId) setBillingSalesPointId(user.salesPointId);
      }
    }
  }, [canChangeScope, user?.branchId, user?.salesPointId, activeSaleId]);

  useEffect(() => {
    if (!activeSaleId && selectedSalesPoint) {
      setBillingSalesPointId(selectedSalesPoint);
    }
  }, [selectedSalesPoint, activeSaleId]);

  const pendingItems = useMemo(
    () => cartItems.filter((item) => !item.locked),
    [cartItems]
  );
  const billItems = useMemo(
    () => cartItems.filter((item) => item.locked),
    [cartItems]
  );

  const cartSubtotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const showToast = (message: string, severity: "success" | "error") =>
    setToast({ message, severity });

  const mapSaleItemsToCart = useCallback(
    (sale: Record<string, unknown>): CartItem[] => {
      const rawItems = (sale.saleItems ?? sale.items ?? []) as Array<
        Record<string, unknown>
      >;
      return rawItems.map((item) => {
        const product = item.product as Record<string, unknown> | undefined;
        return {
          id: String(item.id ?? item.productId),
          productId: String(item.productId ?? ""),
          name: String(product?.name ?? "Item"),
          price: toAmount(item.unitPrice),
          quantity: toAmount(item.quantity),
          locked: true,
          kotNumber: (item.kotNumber as string | null) ?? null,
          kotStatus: (item.kotStatus as string | null) ?? null,
          gstPercentage: toAmount(item.gstPercentage ?? product?.gstPercentage),
          isTaxInclusive: Boolean(
            item.isTaxInclusive ?? product?.isTaxInclusive
          ),
        };
      });
    },
    []
  );

  const loadTableBill = useCallback(
    async (saleId: string, keepPending = false) => {
      const sale = await saleAPI.getSaleById(saleId);
      if (sale.salesPointId) {
        setBillingSalesPointId(sale.salesPointId);
      }
      const billLines = mapSaleItemsToCart(sale as unknown as Record<string, unknown>);
      setCartItems((prev) => {
        const pending = keepPending ? prev.filter((item) => !item.locked) : [];
        return [...billLines, ...pending];
      });
    },
    [mapSaleItemsToCart]
  );

  const resetScannedCard = () => {
    scannedCardRef.current = "";
    setScanBuffer("");
    setNfcCardId(null);
    setCardInfo(null);
    setCardScanLoading(false);
    setCardScanSession((s) => s + 1);
  };

  const openTableSale = useCallback(
    async (table: Table) => {
      if (!selectedBranch) {
        showToast("Select a branch first", "error");
        return;
      }

      if (table.currentSaleId) {
        setActiveSaleId(table.currentSaleId);
        await loadTableBill(table.currentSaleId);
        return;
      }

      if (table.backendStatus !== "AVAILABLE" && table.backendStatus !== "RESERVED") {
        showToast(`Table is ${table.backendStatus}`, "error");
        return;
      }

      try {
        setProcessing(true);
        const sale = await saleAPI.createSale({
          branchId: selectedBranch,
          salesPointId: selectedSalesPoint || undefined,
          orderType: "DINE_IN",
          tableId: table.id,
          paymentMethod: "cash",
          notes: customerDetails.name
            ? `Customer: ${customerDetails.name}`
            : undefined,
        });
        setActiveSaleId(sale.id);
        if (sale.salesPointId) {
          setBillingSalesPointId(sale.salesPointId);
        } else if (selectedSalesPoint) {
          setBillingSalesPointId(selectedSalesPoint);
        }
        await refetchTables();
        showToast(`Bill opened on ${table.name}`, "success");
      } catch (err: unknown) {
        showToast(getApiErrorMessage(err, "Failed to open table"), "error");
      } finally {
        setProcessing(false);
      }
    },
    [selectedBranch, selectedSalesPoint, customerDetails.name, refetchTables, loadTableBill]
  );

  const handleSelectTable = async (table: Table) => {
    setSelectedTable(table);
    setCartItems([]);

    const saleId = table.currentSaleId;
    setActiveSaleId(saleId ?? null);

    if (saleId) {
      try {
        setProcessing(true);
        await loadTableBill(saleId);
      } catch {
        showToast("Could not load table bill", "error");
      } finally {
        setProcessing(false);
      }
      return;
    }

    if (table.status === "vacant" || table.backendStatus === "RESERVED") {
      await openTableSale(table);
    }
  };

  const handleAddToCart = (product: Product) => {
    if (!selectedTable) {
      showToast("Select a table first", "error");
      return;
    }
    if (!activeSaleId) {
      showToast("Open table bill first", "error");
      return;
    }

    const existingPending = cartItems.find(
      (item) => !item.locked && item.productId === product.productMasterId
    );
    if (existingPending) {
      setCartItems(
        cartItems.map((item) =>
          item.id === existingPending.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      setCartItems([
        ...cartItems,
        {
          id: `pending-${product.productMasterId}-${Date.now()}`,
          productId: product.productMasterId,
          name: product.name,
          price: product.salePrice,
          quantity: 1,
          locked: false,
          gstPercentage: product.gstPercentage ?? 0,
          isTaxInclusive: Boolean(product.isTaxInclusive),
        },
      ]);
    }
  };

  const handleUpdateQuantity = (itemId: string, delta: number) => {
    setCartItems(
      cartItems
        .map((item) => {
          if (item.id !== itemId || item.locked) return item;
          return { ...item, quantity: Math.max(0, item.quantity + delta) };
        })
        .filter((item) => item.locked || item.quantity > 0)
    );
  };

  const handleRemoveItem = (itemId: string) => {
    setCartItems(cartItems.filter((item) => item.id !== itemId || item.locked));
  };

  const handlePrintKOT = async () => {
    if (!activeSaleId) {
      showToast("No active bill on this table", "error");
      return;
    }
    if (pendingItems.length === 0) {
      showToast("Add new items before sending KOT", "error");
      return;
    }

    try {
      setProcessing(true);
      const result = await saleAPI.addKot(activeSaleId, {
        items: pendingItems.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          unitPrice: item.price,
          totalPrice: item.price * item.quantity,
          netAmount: item.price * item.quantity,
        })),
        notes: selectedTable ? `Table ${selectedTable.name}` : undefined,
      });
      setKotNumber(result.kotNumber);
      setKotDialog(true);
      await loadTableBill(activeSaleId);
      showToast(
        result.kotNumber
          ? `KOT sent: ${result.kotNumber}`
          : "Items added to bill",
        "success"
      );
    } catch (err: unknown) {
      showToast(getApiErrorMessage(err, "KOT failed"), "error");
    } finally {
      setProcessing(false);
    }
  };

  const handleSettleBill = () => {
    if (!activeSaleId) {
      showToast("No active bill", "error");
      return;
    }
    setSettleError(null);
    setPaymentMethod(isMerchant ? "card" : "cash");
    resetScannedCard();
    setSettleOpen(true);
  };

  const handleCardScan = async (number: string) => {
    scannedCardRef.current = number.trim();
    setScanBuffer("");
    setCardInfo(null);
    setNfcCardId(null);
    setCardScanLoading(true);
    try {
      const card = await nfcCardAPI.getCardByNumber(number);
      setNfcCardId(card.id);
      setCardInfo({
        displayNumber: card.displayNumber,
        balance: toAmount(card.balance),
        holderName: card.holder?.holderName,
      });
    } catch {
      resetScannedCard();
      showToast("Card not found", "error");
    } finally {
      setCardScanLoading(false);
    }
  };

  const handleConfirmSettle = async () => {
    if (!activeSaleId) return;
    if (paymentMethod === "card" && !nfcCardId) {
      showToast("Scan NFC card to settle", "error");
      return;
    }

    try {
      setProcessing(true);
      setSettleError(null);

      if (pendingItems.length > 0) {
        await saleAPI.addKot(activeSaleId, {
          items: pendingItems.map((item) => ({
            productId: item.productId,
            quantity: item.quantity,
            unitPrice: item.price,
            totalPrice: item.price * item.quantity,
            netAmount: item.price * item.quantity,
          })),
        });
      }

      await saleAPI.completeSale(activeSaleId, {
        paymentMethod,
        taxAmount: 0,
        discountAmount: 0,
        ...(customerDetails.customerId
          ? { customerId: customerDetails.customerId }
          : {}),
        ...(customerDetails.name.trim()
          ? { customerName: customerDetails.name.trim() }
          : {}),
        ...(paymentMethod === "card" && nfcCardId ? { nfcCardId } : {}),
      });

      const settledTableId = selectedTable?.id;
      const settledSaleId = activeSaleId;

      showToast("Bill settled successfully", "success");
      setSettleOpen(false);
      setSelectedTable(null);
      setActiveSaleId(null);
      setCartItems([]);
      resetScannedCard();
      setCustomerDetails({ name: "", phone: "", email: "", customerId: null });

      queryClient.setQueryData(
        ["restaurant-tables", selectedBranch, selectedSalesPoint],
        (old: { tables?: Array<Record<string, unknown>> } | undefined) => {
          if (!old?.tables) return old;
          return {
            ...old,
            tables: old.tables.map((row) => {
              const id = String(row.id ?? "");
              const currentSaleId = row.currentSaleId ?? null;
              if (id !== settledTableId && currentSaleId !== settledSaleId) {
                return row;
              }
              return {
                ...row,
                status: "AVAILABLE",
                currentSaleId: null,
                currentSale: null,
              };
            }),
          };
        }
      );

      await refetchTables();
      queryClient.invalidateQueries({ queryKey: ["restaurant-tables"] });
    } catch (err: unknown) {
      const message = getApiErrorMessage(err, "Settlement failed");
      setSettleError(message);
      showToast(message, "error");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        bgcolor: petpooja.bg,
        overflow: "hidden",
      }}
    >
      {/* Petpooja-style top bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 0.75,
          bgcolor: petpooja.header,
          color: petpooja.headerText,
          flexShrink: 0,
          borderBottom: `3px solid ${petpooja.accent}`,
        }}
      >
        <AppLogo size="sm" />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={800} noWrap>
            Restaurant POS
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.75 }}>
            {branchName} · {deptName}
          </Typography>
        </Box>

        <Chip
          icon={<RestaurantIcon sx={{ color: "#fff !important", fontSize: 16 }} />}
          label="Dine In"
          size="small"
          sx={{
            bgcolor: petpooja.accent,
            color: "#fff",
            fontWeight: 700,
            display: { xs: "none", sm: "flex" },
          }}
        />

        <Typography
          variant="caption"
          sx={{ display: { xs: "none", md: "block" }, opacity: 0.75, ml: 1 }}
        >
          {clock}
        </Typography>

        <Box sx={{ flex: 1 }} />

        {canChangeScope ? (
          <>
            <FormControl size="small" sx={{ minWidth: 120, display: { xs: "none", sm: "block" } }}>
              <Select
                value={selectedBranch}
                onChange={(e) => {
                  if (activeSaleId) {
                    showToast("Settle the open bill before changing branch", "error");
                    return;
                  }
                  setSelectedBranch(e.target.value);
                  setSelectedSalesPoint("");
                  setBillingSalesPointId("");
                  setSelectedTable(null);
                  setActiveSaleId(null);
                  setCartItems([]);
                }}
                disabled={branchesLoading}
                displayEmpty
                sx={{
                  fontSize: "0.8125rem",
                  color: "#fff",
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.25)",
                  },
                  ".MuiSvgIcon-root": { color: "#fff" },
                }}
              >
                {branches.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              size="small"
              sx={{ minWidth: 130, display: { xs: "none", md: "block" } }}
            >
              <Select
                value={selectedSalesPoint}
                onChange={(e) => {
                  if (activeSaleId) {
                    showToast("Settle the open bill before changing salesPoint", "error");
                    return;
                  }
                  setSelectedSalesPoint(e.target.value);
                  setBillingSalesPointId(e.target.value);
                  setSelectedTable(null);
                  setActiveSaleId(null);
                  setCartItems([]);
                }}
                disabled={!selectedBranch || salesPointsLoading || !!activeSaleId}
                displayEmpty
                sx={{
                  fontSize: "0.8125rem",
                  color: "#fff",
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.25)",
                  },
                  ".MuiSvgIcon-root": { color: "#fff" },
                }}
              >
                {salesPoints.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        ) : (
          <Chip
            label={`${branchName} · ${deptName}`}
            size="small"
            sx={{
              display: { xs: "none", sm: "flex" },
              bgcolor: "rgba(255,255,255,0.12)",
              color: "#fff",
              fontWeight: 600,
              maxWidth: 240,
            }}
          />
        )}

        <Button
          size="small"
          variant="outlined"
          startIcon={<PointOfSaleIcon />}
          onClick={() => navigate("/sales")}
          sx={{
            color: "#fff",
            borderColor: "rgba(255,255,255,0.35)",
            fontSize: "0.75rem",
            display: { xs: "none", lg: "flex" },
          }}
        >
          Retail POS
        </Button>
        <Button
          size="small"
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/dashboard")}
          sx={{ color: "#fff", fontSize: "0.75rem" }}
        >
          Exit
        </Button>
      </Box>

      <Box sx={{ flex: 1, display: "flex", minHeight: 0, overflow: "hidden" }}>
        {!selectedBranch ? (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
            <Alert severity="info">Select a branch to load restaurant tables.</Alert>
          </Box>
        ) : !selectedSalesPoint ? (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
            <Alert severity="info">Select a salesPoint to load tables for that salesPoint.</Alert>
          </Box>
        ) : tablesLoading ? (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress sx={{ color: petpooja.accent }} />
          </Box>
        ) : tablesError ? (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => refetchTables()}>
                  Retry
                </Button>
              }
            >
              Failed to load tables from server.
            </Alert>
          </Box>
        ) : (
          <>
            <TablePanel
              tables={tables}
              selectedTable={selectedTable}
              onSelectTable={handleSelectTable}
              loading={tablesLoading}
              onRefresh={() => refetchTables()}
              salesPointName={deptName}
              onSetupTables={
                canCreate("tables") ? () => navigate("/tables") : undefined
              }
            />
            <ProductsPanel
              products={products}
              onAddToCart={handleAddToCart}
              loading={productsLoading}
              disabled={!billingSalesPointId || !selectedTable}
            />
            <CartPanel
              selectedTable={selectedTable}
              cartItems={cartItems}
              billItems={billItems}
              pendingItems={pendingItems}
              customerDetails={customerDetails}
              activeSaleId={activeSaleId}
              cartSubtotal={cartSubtotal}
              taxMode={
                salesPoints.find((d) => d.id === selectedSalesPoint)?.taxMode ||
                "CGST_SGST"
              }
              onUpdateQuantity={handleUpdateQuantity}
              onRemoveItem={handleRemoveItem}
              onPrintKOT={handlePrintKOT}
              onSettleBill={handleSettleBill}
              onEditCustomer={() => setCustomerDialog(true)}
              processing={processing}
            />
          </>
        )}
      </Box>

      <CustomerDialog
        open={customerDialog}
        selectedTable={selectedTable}
        cartItems={cartItems}
        customerDetails={customerDetails}
        onClose={() => setCustomerDialog(false)}
        onCustomerDetailsChange={setCustomerDetails}
        onConfirm={() => setCustomerDialog(false)}
      />

      <KOTDialog
        open={kotDialog}
        kotNumber={kotNumber}
        tableName={selectedTable?.name}
        onClose={() => setKotDialog(false)}
      />

      <Dialog
        open={settleOpen}
        onClose={() => !processing && setSettleOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            bgcolor: petpooja.header,
            color: petpooja.headerText,
            fontWeight: 800,
          }}
        >
          Settle Bill — {selectedTable?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          {settleError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {settleError}
            </Alert>
          )}
          <Alert severity="info" sx={{ mb: 2 }}>
            Bill total: <strong>₹{cartSubtotal.toFixed(2)}</strong>
          </Alert>
          {!isMerchant && (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mb: 2 }}>
              {(
                [
                  { id: "cash" as PaymentMethod, label: "Cash", icon: <CashIcon /> },
                  { id: "upi" as PaymentMethod, label: "UPI", icon: <UpiIcon /> },
                  { id: "card" as PaymentMethod, label: "NFC Card", icon: <NfcIcon /> },
                ] as const
              ).map((method) => (
                <Box
                  key={method.id}
                  onClick={() => {
                    setPaymentMethod(method.id);
                    setSettleError(null);
                  }}
                  sx={{
                    border: `2px solid ${
                      paymentMethod === method.id ? petpooja.accent : petpooja.border
                    }`,
                    borderRadius: 1.5,
                    py: 1.25,
                    textAlign: "center",
                    cursor: "pointer",
                    bgcolor:
                      paymentMethod === method.id ? petpooja.accentLight : petpooja.panel,
                    color:
                      paymentMethod === method.id ? petpooja.accentDark : petpooja.text,
                    "& svg": { display: "block", mx: "auto", mb: 0.5 },
                  }}
                >
                  {method.icon}
                  <Typography variant="caption" fontWeight={700}>
                    {method.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          {paymentMethod === "card" ? (
            <>
              <NfcCardScanInput
                key={cardScanSession}
                value={scanBuffer}
                onChange={setScanBuffer}
                onScanComplete={handleCardScan}
                hideScannedValue
                hideInputAfterScan
                loading={cardScanLoading}
                resolvedDisplayNumber={cardInfo?.displayNumber}
                allowRescan
                onClear={() => {
                  resetScannedCard();
                  setSettleError(null);
                }}
                label="NFC Card"
                hint="Tap card on reader"
              />
              {cardInfo && (
                <Alert
                  severity={
                    cardInfo.balance < cartSubtotal ? "warning" : "info"
                  }
                  sx={{ mt: 2 }}
                >
                  {cardInfo.holderName && (
                    <Typography variant="body2">{cardInfo.holderName}</Typography>
                  )}
                  <Typography variant="body2">
                    Balance ₹{cardInfo.balance.toFixed(2)}
                    {cardInfo.balance < cartSubtotal && (
                      <> · Required ₹{cartSubtotal.toFixed(2)}</>
                    )}
                  </Typography>
                </Alert>
              )}
            </>
          ) : (
            <Alert severity="info">Collect payment via {paymentMethod.toUpperCase()}</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setSettleOpen(false)} disabled={processing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmSettle}
            disabled={processing}
            sx={{
              bgcolor: petpooja.greenDark,
              fontWeight: 700,
              px: 3,
              "&:hover": { bgcolor: petpooja.green },
            }}
          >
            {processing ? <CircularProgress size={22} color="inherit" /> : "Complete Payment"}
          </Button>
        </DialogActions>
      </Dialog>

      {toast && (
        <Snackbar
          open
          autoHideDuration={3500}
          onClose={() => setToast(null)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            severity={toast.severity}
            onClose={() => setToast(null)}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default SalesPage;
